/**
 * TanStack Query cache isolation for courses admin preview.
 *
 * The app shares ONE `QueryClient` (`nuxtApp.$queryClient`, `libs/plugins/vue-query.ts`) between the
 * learner and the admin preview, and courses query keys do not encode which identity fetched them.
 * Without a purge, a preview would render learner data cached earlier in the tab (or dehydrated from
 * the learner-authenticated SSR pass), and the learner would see admin draft data after leaving the
 * preview. The admin-preview middleware therefore drops every courses/membership query on preview
 * entry and exit.
 */
import type { QueryClient } from '@tanstack/vue-query'

/**
 * Root (first) query-key segments of the courses / membership query stores
 * (`libs/layers/courses/<layer>/stores/<name>.query-store.ts` and `<layer>/utils/query-keys.utils.ts`). Keep in sync when a
 * courses query store adds a new root key.
 */
export const COURSES_ADMIN_PREVIEW_QUERY_ROOTS: ReadonlySet<string> = new Set([
  'admin-preview',
  'announcements',
  'assessment',
  'assignment',
  'certificates',
  'checkout',
  'comments',
  'courses-access',
  'courses-access-guard',
  'courses-user',
  'integrations',
  'learner-api',
  'learner-comments',
  'library',
  'live-sessions',
  'media-upload',
  'memberships-user-activity',
  'modules',
  'offers',
  'post-completion-learner',
  'posts',
  'product',
  'quiz',
  'search',
  'user-progress',
  'user-purchase',
])

/** Whether a query key belongs to the courses / membership data an admin preview must not share. */
export function isCoursesAdminPreviewQueryKey(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0]
  return typeof root === 'string' && COURSES_ADMIN_PREVIEW_QUERY_ROOTS.has(root)
}

const coursesQueryFilter = {
  predicate: (query: { queryKey: readonly unknown[] }) => isCoursesAdminPreviewQueryKey(query.queryKey),
}

/** Drop cached courses queries (preview entry / exit) so neither identity sees the other's data. */
export function removeCoursesAdminPreviewQueries(queryClient: QueryClient | null | undefined): void {
  queryClient?.removeQueries(coursesQueryFilter)
}

/**
 * Reset courses queries once an async admin session becomes ready: queries mounted while the
 * exchange was in flight ran fail-closed (unauthenticated), so active ones refetch as the admin.
 */
export async function resetCoursesAdminPreviewQueries(queryClient: QueryClient | null | undefined): Promise<void> {
  try {
    await queryClient?.resetQueries(coursesQueryFilter)
  } catch {
    // A refetch error surfaces through the query itself; the reset only re-arms the cache.
  }
}
